/** Quick changes: hours (S70), holiday prompts (S71) and the emergency banner (S72). docs/52 §3.8, §3.9. */
import { z } from 'zod';
import { ClockTime, Day, IsoDateTime, TimeZone, text } from '../common.ts';
import { Weekday } from '../enums.ts';
import { LocationId, NoticeId, RequestId } from '../ids.ts';
import { PromisedBy } from './shared.ts';

export const Interval = z
  .object({ start: ClockTime, end: ClockTime })
  .strict()
  .refine((i) => i.start < i.end, { message: 'the end must be after the start', path: ['end'] })
  .meta({ examples: [{ start: '08:00', end: '17:00' }] });

export const DayHours = z
  .object({ day: Weekday, open: z.boolean(), allDay: z.boolean(), intervals: z.array(Interval).max(3) })
  .strict()
  .refine((d) => !d.open || d.allDay || d.intervals.length > 0, { message: 'add opening times or mark it closed', path: ['intervals'] });

export const Closure = z
  .object({ from: Day, to: Day, label: text(60).optional() })
  .strict()
  .refine((c) => c.from <= c.to, { message: 'the end date must be on or after the start', path: ['to'] });

export const Hours = z.object({
  locationId: LocationId.nullable(),
  timeZone: TimeZone,
  weekly: z.array(DayHours).length(7),
  closures: z.array(Closure.and(z.object({ id: z.string().max(64) }))),
  /** An open hours request, if one is in flight. */
  pendingRequestId: RequestId.nullable(),
  /** Hours sent before this time go live the same business day (3 pm Eastern, docs/44 §3.3). */
  sameDayCutoff: IsoDateTime,
});

export const HoursQuery = z.object({ location: LocationId.optional() }).strict();

export const HoursUpdateBody = z
  .object({ locationId: LocationId.optional(), weekly: z.array(DayHours).length(7), closures: z.array(Closure).max(30) })
  .strict();

export const HoursUpdated = z.object({ requestId: RequestId }).extend(PromisedBy.shape);

export const USHoliday = z.enum([
  'new_years_day',
  'mlk_day',
  'presidents_day',
  'memorial_day',
  'juneteenth',
  'independence_day',
  'labor_day',
  'columbus_day',
  'veterans_day',
  'thanksgiving',
  'christmas',
]);

export const HolidayBody = z.discriminatedUnion('answer', [
  z.object({ answer: z.literal('closed'), holiday: USHoliday, date: Day, locationId: LocationId.optional() }).strict(),
  z.object({ answer: z.literal('normal'), holiday: USHoliday, date: Day, locationId: LocationId.optional() }).strict(),
  z.object({ answer: z.literal('custom'), holiday: USHoliday, date: Day, intervals: z.array(Interval).min(1).max(3), locationId: LocationId.optional() }).strict(),
]);

export const NoticeTemplate = z.enum(['emergency_calls', 'freeze_warning', 'closed_today']);

export const NoticeCreateBody = z
  .object({ template: NoticeTemplate, showUntil: Day, locationId: LocationId.optional() })
  .strict();

export const NoticeView = z.object({
  id: NoticeId,
  template: NoticeTemplate,
  text: z.string().max(200),
  startsAt: IsoDateTime,
  endsAt: IsoDateTime,
  state: z.enum(['awaiting_check', 'live', 'ended']),
});
export const NoticeList = z.object({ items: z.array(NoticeView), templates: z.array(z.object({ template: NoticeTemplate, preview: z.string().max(200) })) });
export const NoticeCreated = z.object({ notice: NoticeView, requestId: RequestId });
